import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BrandedLogo } from "@/components/BrandedLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WelcomePopupModal } from "@/components/WelcomePopupModal";
import { WaveformDivider } from "@/components/WaveformDivider";
import { Button } from "@/components/ui/button";
import { LazySection } from "@/components/LazySection";
import { MobileMenu } from "@/components/MobileMenu";
import { SubscribeDialog } from "@/components/SubscribeDialog";
import { SocialShareButtons } from "@/components/SocialShareButtons";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";

// Navigation links matching Header.tsx for consistency
const navLinks = [
  { href: "/python-newsletter/", label: "Newsletter", preload: () => import("@/pages/PythonNewsletterPage") },
  { href: "/salary/", label: "Salary Guide", preload: () => import("@/pages/SalaryHub") },
  { href: "/hiring-trends/", label: "Market Trends", preload: () => import("@/pages/HiringTrends") },
  { href: "/interview-prep/", label: "Interview Prep", preload: () => import("@/pages/InterviewPrepHubPage") },
  { href: "/hiring-playbooks/", label: "Hiring Playbooks", preload: () => import("@/pages/HiringPlaybooksHub") },
  { href: "/cv-reviewer/", label: "CV Analyzer", preload: () => import("@/pages/CVReviewerPage") },
  { href: "/linkedin-analyzer/", label: "LinkedIn", preload: () => import("@/pages/LinkedInAnalyzerPage") },
  { href: "/about/", label: "About", preload: () => import("@/pages/AboutPage") },
];

const preloadPodcast = () => import("@/pages/PodcastPage");
const preloadNewsletters = () => import("@/pages/NewslettersPage");

import pythonLogo from "@/assets/python-logo-new.png";
import snakeSignalsLogo from "@/assets/snake-signals-logo-clean.png";

// Critical icons for above-the-fold content
import { 
  Mail,
  Command,
  BarChart3,
  TrendingUp,
  Calendar,
  Zap,
  Sparkles,
} from "lucide-react";

// Lazy load all heavy components and sections
const UnifiedCVTool = lazy(() => import("@/components/UnifiedCVTool").then(module => ({ default: module.UnifiedCVTool })));
const SalaryGuide = lazy(() => import("@/components/SalaryGuide").then(module => ({ default: module.SalaryGuide })));
const MarketSignals = lazy(() => import("@/components/MarketSignals").then(module => ({ default: module.MarketSignals })));
const InterviewPrepHub = lazy(() => import("@/components/InterviewPrepHub").then(module => ({ default: module.InterviewPrepHub })));
const CodingChallenges = lazy(() => import("@/components/CodingChallenges").then(module => ({ default: module.CodingChallenges })));
const StackGuides = lazy(() => import("@/components/StackGuides").then(module => ({ default: module.StackGuides })));
const HiringPlaybooks = lazy(() => import("@/components/HiringPlaybooks").then(module => ({ default: module.HiringPlaybooks })));
const MarketHeatmap = lazy(() => import("@/components/MarketHeatmap").then(module => ({ default: module.MarketHeatmap })));
const DeveloperPreferences = lazy(() => import("@/components/DeveloperPreferences").then(module => ({ default: module.DeveloperPreferences })));
const NewsletterSubscribe = lazy(() => import("@/components/NewsletterSubscribe").then(module => ({ default: module.NewsletterSubscribe })));
const AnswerSummary = lazy(() => import("@/components/layout/AnswerSummary").then(module => ({ default: module.AnswerSummary })));

// Lazy load below-the-fold sections
const AboutSection = lazy(() => import("./sections/AboutSection"));
const ReferralSection = lazy(() => import("@/components/ReferralSection"));
const WhatInsideSection = lazy(() => import("./sections/WhatInsideSection"));
const ArchiveSection = lazy(() => import("./sections/ArchiveSection"));
const LatestPodcast = lazy(() => import("@/components/LatestPodcast").then(module => ({ default: module.LatestPodcast })));
const JobOfTheWeek = lazy(() => import("@/components/JobOfTheWeek"));
const StandOutSection = lazy(() => import("@/components/StandOutSection").then(module => ({ default: module.StandOutSection })));

const Index = () => {
  const { theme } = useTheme();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const resolvedTheme: 'light' | 'dark' = theme === 'system'
    ? (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  const heroLogoSrc = snakeSignalsLogo;

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem("snake-signals-popup-shown");
    
    if (!hasSeenPopup) {
      // Show modal after a short delay
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground scroll-smooth">
      {/* Dynamic SEO for Homepage */}
      <Helmet>
        <title>Snake Signals | Python Salaries & Hiring Intelligence 2026</title>
        <meta name="description" content="Weekly Python market intelligence: verified salary benchmarks for the UK, US and Europe, interview prep, and hiring playbooks." />
        <link rel="canonical" href="https://snakesignals.com/" />
        <meta property="og:title" content="Snake Signals | Python Salaries & Hiring Intelligence 2026" />
        <meta property="og:description" content="Weekly Python market intelligence: verified salary benchmarks for the UK, US and Europe, interview prep, and hiring playbooks." />
        <meta property="og:url" content="https://snakesignals.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Snake Signals" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Snake Signals | Python Salaries & Hiring Intelligence 2026" />
        <meta name="twitter:description" content="Weekly Python market intelligence for developers and hiring managers." />
        
        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Snake Signals",
            "url": "https://snakesignals.com",
            "logo": "https://snakesignals.com/lovable-uploads/snake-signals-logo.png",
            "description": "Weekly Python hiring market intelligence for developers and hiring managers",
            "founder": {
              "@type": "Person",
              "name": "Josh Smith",
              "jobTitle": "Python Recruitment Specialist",
              "url": "https://snakesignals.com/about/"
            },
            "parentOrganization": {
              "@type": "Organization",
              "name": "Understanding Recruitment",
              "url": "https://www.understandingrecruitment.com/"
            },
            "sameAs": [
              "https://www.linkedin.com/in/python-recruitment/",
              "https://snakesignals.beehiiv.com/"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "jsmith@understandingrecruitment.com",
              "contactType": "customer service"
            }
          })}
        </script>
        
        {/* WebSite Schema with SearchAction */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Snake Signals",
            "url": "https://snakesignals.com",
            "description": "Python developer salary guide, hiring intelligence, and career tools",
            "publisher": {
              "@type": "Organization",
              "name": "Snake Signals"
            }
          })}
        </script>
      </Helmet>
      
      <WelcomePopupModal 
        isOpen={showWelcomeModal} 
        onClose={() => setShowWelcomeModal(false)} 
      />

      {/* Floating Navigation Anchors */}
      <div className="floating-nav hidden xl:flex flex-col gap-2" role="navigation" aria-label="Section quick links">
        <a href="#salary-guide" className="p-2 rounded-full hover:bg-primary/20 transition-colors" title="Salary Guide" aria-label="Jump to Salary Guide section">
          <BarChart3 size={20} className="text-primary" />
        </a>
        <a href="#market-signals" className="p-2 rounded-full hover:bg-accent/20 transition-colors" title="Market Signals" aria-label="Jump to Market Signals section">
          <TrendingUp size={20} className="text-accent" />
        </a>
        <a href="#stand-out" className="p-2 rounded-full hover:bg-primary/20 transition-colors" title="Stand Out" aria-label="Jump to Stand Out section">
          <Sparkles size={20} className="text-primary" />
        </a>
        <a href="#archive" className="p-2 rounded-full hover:bg-secondary/20 transition-colors" title="Archive" aria-label="Jump to Newsletter Archive section">
          <Calendar size={20} className="text-secondary-foreground" />
        </a>
        <a href="#subscribe" className="p-2 rounded-full hover:bg-primary/20 transition-colors" title="Subscribe" aria-label="Jump to Subscribe section">
          <Zap size={20} className="text-primary" />
        </a>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-background/85 backdrop-blur-xl border-b border-border" role="navigation" aria-label="Main navigation">
          <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0">
            <BrandedLogo size="sm" showSnakeSignals={true} showPoweredBy={false} />
          </Link>
          
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link 
                key={link.href}
                to={link.href} 
                className="font-mono text-sm hover:text-primary transition-colors"
                aria-label={link.label}
                onMouseEnter={() => link.preload()}
                onFocus={() => link.preload()}
              >
                {link.label}
              </Link>
            ))}
            <Link 
              to="/podcast/"
              className="font-mono text-sm hover:text-primary transition-colors"
              aria-label="Podcast"
              onMouseEnter={() => preloadPodcast()}
              onFocus={() => preloadPodcast()}
            >
              Podcast
            </Link>
            <Link 
              to="/newsletters/"
              className="font-mono text-sm hover:text-primary transition-colors"
              aria-label="Newsletter Archive"
              onMouseEnter={() => preloadNewsletters()}
              onFocus={() => preloadNewsletters()}
            >
              Archive
            </Link>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <SubscribeDialog>
              <Button
                variant="default"
                size="sm"
                className="hidden sm:inline-flex gap-2 font-mono"
              >
                <Zap size={16} />
                Join Now
              </Button>
            </SubscribeDialog>
            <MobileMenu />
          </div>
        </div>
      </nav>


      {/* Hero Section */}
      <header id="hero" className="min-h-screen flex items-center justify-center px-3 sm:px-6 lg:px-8 bg-gradient-to-b from-background via-background to-background relative overflow-hidden pt-20 sm:pt-28" role="banner" aria-label="Snake Signals Python Newsletter">
        {/* Background elements */}
        <div className="absolute left-0 right-0 top-[62%] sm:top-[58%] flex items-center justify-center opacity-25 pointer-events-none" aria-hidden="true">
          <div className="signal-line h-1 w-full max-w-4xl rounded-full"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center z-20 pb-12 sm:pb-20">
          
          {/* Logo + Wordmark (single baseline) */}
          <div className="mb-5 sm:mb-6">
            <h1 className="font-bold bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent animate-fade-in font-orbitron glow-title whitespace-nowrap leading-none text-[clamp(2.25rem,7vw,4.75rem)]">
              <img
                src={heroLogoSrc}
                alt="Snake Signals logo"
                className="inline-block align-middle h-[1.4em] w-[1.4em] object-contain scale-[1.45] translate-y-[0.06em] mr-3 sm:mr-4"
              />
              <span className="align-middle">Snake Signals</span>
            </h1>
          </div>
          
          {/* Python logo and newsletter text */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-7 sm:mb-9">
            <img 
              src={pythonLogo} 
              alt="Python Programming Language Logo - Official Python Powered Badge" 
              className="w-10 h-6 sm:w-12 sm:h-8 object-contain"
              width="48"
              height="32"
            />
            <p className="text-base sm:text-lg md:text-xl text-primary font-orbitron" role="text" aria-label="The number one Python newsletter">
              The #1 Python Newsletter for 2026
            </p>
          </div>
          
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 max-w-3xl mx-auto animate-fade-in animation-delay-500 font-sora leading-relaxed summary px-2" role="text">
            Weekly Python salary data, hiring trends, and market intelligence for developers and hiring managers in 2026. Get the insider edge for Python careers and recruitment in the UK, USA, and Europe.
          </p>
          
          <nav className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center animate-fade-in animation-delay-1000 mb-6 sm:mb-8 px-2" aria-label="Primary call to action">
            <SubscribeDialog>
              <Button
                id="signals-cta"
                size="lg"
                className="glow-cta font-semibold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-space transition-all duration-300 w-full sm:w-auto"
              >
                <Zap className="mr-2" size={18} />
                Send me the signals
              </Button>
            </SubscribeDialog>
            <Button 
              variant="outline" 
              size="lg" 
              className="px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-space w-full sm:w-auto"
              asChild
            >
              <a href="#archive" aria-label="Browse Snake Signals newsletter archive">
                Browse archive
              </a>
            </Button>
          </nav>
          
          <div className="flex items-center justify-center gap-4">
            <span className="text-muted-foreground text-sm">powered by</span>
            <a 
              href="https://www.understandingrecruitment.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              aria-label="Visit Understanding Recruitment Group website"
            >
              <img 
                src="/lovable-uploads/d9cd7f2e-a553-451e-a91c-2010e4d5866a.png"
                alt="Understanding Recruitment Group - Leading Python Recruitment Specialists"
                className="h-20 object-contain"
                width="160"
                height="80"
              />
            </a>
          </div>
        </div>
        
        <WaveformDivider className="absolute bottom-0" animated />
      </header>

      {/* Key Takeaways for SEO/GEO - AI Extractable Summary */}
      <section className="container mx-auto px-4 py-8" aria-label="Python Career Key Facts 2026">
        <Suspense fallback={null}>
          <AnswerSummary 
            title="Python Market Intelligence 2026"
            points={[
              "Python developer salaries: UK £40K-£140K, USA $80K-$300K, EU €40K-€140K based on experience level",
              "FastAPI leads at 38% adoption (+9% YoY), overtaking Flask as #1 Python web framework",
              "AI/ML Python specialists earn 15-25% premium; FinTech developers earn 20-40% above base",
              "91% of successful job postings now include salary bands — transparency is mandatory",
              "London demand up 32% YoY, Amsterdam +35%, Berlin +28% — European Python market booming",
              "Free CV analyzer and salary estimator tools available to benchmark your market value"
            ]}
          />
        </Suspense>
      </section>

      {/* Latest Podcast Section */}
      <LazySection id="podcast" rootMargin="200px">
        <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading podcast...</div></div>}>
          <LatestPodcast />
        </Suspense>
      </LazySection>

      {/* About Josh Smith - Intersection Observer Lazy Loading */}
      <LazySection id="about" rootMargin="300px">
        <article aria-labelledby="about-heading">
          <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
            <AboutSection />
          </Suspense>
        </article>
      </LazySection>

      {/* Job of the Week */}
      <LazySection id="job-of-the-week" rootMargin="300px">
        <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <JobOfTheWeek />
        </Suspense>
      </LazySection>

      {/* Referral Section - Hire Python Developers */}
      <LazySection id="referral" rootMargin="300px">
        <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <ReferralSection />
        </Suspense>
      </LazySection>

      {/* What's Inside - Intersection Observer Lazy Loading */}
      <LazySection id="whats-inside" rootMargin="300px">
        <section aria-labelledby="whats-inside-heading">
          <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
            <WhatInsideSection />
          </Suspense>
        </section>
      </LazySection>

      {/* Archive Section - Intersection Observer Lazy Loading */}
      <LazySection id="archive" rootMargin="400px">
        <section aria-labelledby="archive-heading">
          <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading Archive...</div></div>}>
            <ArchiveSection />
          </Suspense>
        </section>
      </LazySection>

      {/* CV Analyser Section */}
      <LazySection id="cv-analyzer" className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="400px">
        <section id="cv-analyser" className="scroll-mt-20" aria-labelledby="cv-analyzer-heading" aria-describedby="cv-analyzer-description">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading CV Analyser...</div></div>}>
            <UnifiedCVTool />
          </Suspense>
        </section>
      </LazySection>

      {/* Salary Guide Section */}
      <LazySection id="salary-guide" className="section-spacing px-4 sm:px-6 lg:px-8 bg-card/10" rootMargin="400px">
        <section className="scroll-mt-20" aria-labelledby="salary-guide-heading" aria-describedby="salary-guide-description">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Salary Guide...</div></div>}>
            <SalaryGuide />
          </Suspense>
        </section>
      </LazySection>

      {/* Market Signals Section - Intersection Observer Lazy Loading */}
      <LazySection id="market-signals" className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="400px">
        <section className="scroll-mt-20" aria-labelledby="market-signals-heading" aria-describedby="market-signals-description">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Market Signals...</div></div>}>
            <MarketSignals />
          </Suspense>
        </section>
      </LazySection>

      {/* Interview Prep Hub - Load more eagerly */}
      <LazySection id="interview-prep" className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="800px">
        <section aria-labelledby="interview-prep-heading">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Interview Prep...</div></div>}>
            <InterviewPrepHub />
          </Suspense>
        </section>
      </LazySection>

      {/* Stand Out in the AI Job Market - Junior career signals */}
      <LazySection id="stand-out-wrap" rootMargin="500px">
        <Suspense fallback={<div className="section-spacing flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <StandOutSection />
        </Suspense>
      </LazySection>

      {/* Coding Challenges - Trending interview patterns */}
      <LazySection id="coding-challenges" className="section-spacing px-4 sm:px-6 lg:px-8 bg-card/10" rootMargin="500px">
        <section aria-labelledby="coding-challenges-heading">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Coding Challenges...</div></div>}>
            <CodingChallenges />
          </Suspense>
        </section>
      </LazySection>

      {/* Hiring Playbooks - Intersection Observer Lazy Loading */}
      <LazySection id="hiring-playbooks" className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="500px">
        <section aria-labelledby="hiring-playbooks-heading">
          <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Hiring Playbooks...</div></div>}>
            <HiringPlaybooks />
          </Suspense>
        </section>
      </LazySection>

      {/* Market Heatmap - Intersection Observer Lazy Loading */}
      <LazySection className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="500px">
        <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Market Heatmap...</div></div>}>
          <MarketHeatmap />
        </Suspense>
      </LazySection>

      {/* Stack Guides - Intersection Observer Lazy Loading */}
      <LazySection className="section-spacing px-4 sm:px-6 lg:px-8 bg-card/10" rootMargin="500px">
        <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Stack Guides...</div></div>}>
          <StackGuides />
        </Suspense>
      </LazySection>

      {/* Developer Preferences - Intersection Observer Lazy Loading */}
      <LazySection className="section-spacing px-4 sm:px-6 lg:px-8" rootMargin="500px">
        <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Developer Preferences...</div></div>}>
          <DeveloperPreferences />
        </Suspense>
      </LazySection>

      {/* Newsletter Subscribe Section */}
      <LazySection id="subscribe" className="section-spacing px-4 sm:px-6 lg:px-8 bg-card/10" rootMargin="500px">
        <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-pulse text-muted-foreground">Loading Subscribe Form...</div></div>}>
          <NewsletterSubscribe />
        </Suspense>
      </LazySection>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border" role="contentinfo">
        <div className="container mx-auto text-center">
          <p className="text-muted-foreground text-sm mb-2">
            Snake Signals - The Python Developer's Edge
          </p>
          <p className="text-muted-foreground/60 text-xs">
            © 2026 Understanding Recruitment Group. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
